import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, TextInput } from 'react-native';
import { DataStore } from 'aws-amplify';
import { AppUser, UserType, UserGroup } from '../../models';
import { Picker } from '@react-native-picker/picker';
import * as DataStoreUtil from '../../components/DataStoreUtil';
import * as CmnUtil from '../../components/CmnUtil';
import * as CognitoControl from '../../components/CognitoControl';
import * as AppConst from '../../components/AppConst';

/** ================================
*  ユーザー追加画面
*  ================================*/
const AddUser = ({ closeFunction }: { closeFunction: Function }) => {
  // 各種情報
  const [username, setUsername] = useState('');
  const [mailAddress, setMailAddress] = useState('');
  const [userType, setUserType] = useState<string>('');
  const [groupList, setGroupList] = useState<UserGroup[]>([]);
  const [group, setGroup] = useState<string>('');

  useEffect(() => {
    (async () => {
      if (userType === UserType.PRODUCER || userType === UserType.INSTRUCTOR) {
        const groups = (await DataStore.query(UserGroup, c => c.groupType('eq', userType)));
        setGroupList(groups);
      } else {
        setGroupList([]);
      }
      setGroup('');
    })();
  }, [userType]);

  // ユーザー登録メイン
  const addUser = async () => {
    CmnUtil.startLoading();
    // エラーチェック
    if (username.length == 0) {
      CmnUtil.appAlert('ユーザー名は必須入力です。');
      CmnUtil.endLoading();
      return;
    }
    if (mailAddress.length == 0) {
      CmnUtil.appAlert('メールアドレスは必須入力です。');
      CmnUtil.endLoading();
      return;
    }
    if (userType.length == 0) {
      CmnUtil.appAlert('タイプは必須入力です。');
      CmnUtil.endLoading();
      return;
    }
    if (group.length == 0) {
      CmnUtil.appAlert('組織は必須入力です。');
      CmnUtil.endLoading();
      return;
    }
    if (!CmnUtil.isMailFormat(mailAddress)) {
      CmnUtil.appAlert('不正な形式のメールアドレスです。');
      CmnUtil.endLoading();
      return;
    }
    const appUsers = (await DataStore.query(AppUser, c => c.authUserID('eq', mailAddress)));
    if (appUsers.length > 0) {
      CmnUtil.appAlert('登録済みのメールアドレスです。');
      CmnUtil.endLoading();
      return;
    }

    // ユーザーデータ作成
    const result = await addUserData();
    if (!result) {
      CmnUtil.appAlert('ユーザー登録に失敗しました。');
      CmnUtil.endLoading();
      return;
    }

    CmnUtil.endLoading();
    CmnUtil.appMsg('ユーザーを登録しました。');
    closeFunction();
  }

  // DataStoreへの登録
  const addUserData = async () => {
    const userGroup = (await DataStore.query(UserGroup, group));
    if (userGroup == undefined) {
      console.log('グループ削除済み');
      return false;
    }

    // cognitoユーザーの登録
    const attrs = [
      { Name: 'email', Value: mailAddress },
      { Name: 'email_verified', Value: 'true' },
    ];
    try {
      await CognitoControl.createUser(mailAddress, attrs);
    } catch (err) {
      console.log(err);
      return false;
    }

    // AppUserの登録
    // 画面から削除する手段を残すために、グループ追加より先に行う
    const tmpType = userType == UserType.INSTRUCTOR ? UserType.INSTRUCTOR : UserType.PRODUCER;
    const newUser = await DataStore.save(new AppUser({
      username: username,
      authUsername: mailAddress,
      authUserID: mailAddress,
      type: tmpType,
      isEnabled: true,
      group: userGroup,
    }));

    // cognitoユーザーをグループに追加
    const groupname = userType == UserType.INSTRUCTOR ? AppConst.cognitoGroup.instructor : AppConst.cognitoGroup.producer;
    try {
      await CognitoControl.addUserToGroup(mailAddress, groupname);
    } catch (err) {
      console.log(err);
      return false;
    }

    return true;
  }

  return (
    <View style={AppConst.styles.dialog}>
      <View style={AppConst.styles.dialogContents}>
        <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 450, borderRadius: 10 }}>
          <AppConst.DialogHeader closeFunction={closeFunction} />

          <View style={{ padding: 10 }}>
            <View style={styles.box}>
              <Text>ユーザー名</Text>
              <TextInput
                style={styles.textInput}
                value={username}
                onChangeText={setUsername}
                textContentType='none'
                autoCapitalize='none'
              />
            </View>

            <View style={styles.box}>
              <Text>メールアドレス</Text>
              <TextInput
                style={styles.textInput}
                value={mailAddress}
                onChangeText={setMailAddress}
                textContentType='emailAddress'
                autoCapitalize='none'
              />
            </View>

            <View style={styles.box}>
              <Text>タイプ</Text>
              <Picker
                selectedValue={userType}
                style={styles.textInput}
                onValueChange={(itemValue, itemIndex) =>
                  setUserType(itemValue)
                }>
                <Picker.Item label='選択してください' value='' />
                <Picker.Item label={DataStoreUtil.convUserType(UserType.INSTRUCTOR)} value={UserType.INSTRUCTOR} />
                <Picker.Item label={DataStoreUtil.convUserType(UserType.PRODUCER)} value={UserType.PRODUCER} />
              </Picker>
            </View>

            <View style={styles.box}>
              <Text>組織</Text>
              <Picker
                selectedValue={group}
                style={styles.textInput}
                onValueChange={(itemValue, itemIndex) =>
                  setGroup(itemValue)
                }>
                <Picker.Item label='選択してください' value='' />
                {groupList.map((data: UserGroup, index: number) => (
                  <Picker.Item key={index} label={data.groupName} value={data.id} />
                ))}
              </Picker>
            </View>

            <TouchableOpacity style={styles.addButton} onPress={addUser}>
              <Text style={{ color: AppConst.defaultTextColor }}>ユーザー登録</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    marginTop: 10,
  },
  textInput: {
    borderBottomWidth: 1,
    backgroundColor: AppConst.defaultBaseInputColor,
    height: 30,
    paddingLeft: 5,
  },
  addButton: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: AppConst.defaultColor,
    height: 30,
    marginTop: 30,
    marginBottom: 20,
  },
});

export default AddUser;
